/**
 * Makes tiles, cards and rows drag sources. A drag writes the same private
 * payload the sidebar tree writes (`writeItemDragPayload`), so items dragged
 * from either surface drop on the other. Dragging a selected item drags the
 * whole loaded selection. The chat composer reads the same payload and adds
 * the items as context (`droppedContexts`). The drag image is a chip with the item's visual,
 * its name and, for several items, their count.
 *
 * Not draggable at all: an item the user cannot edit, and anything on a
 * touch-first device (drag and drop is desktop only; the same pointer query
 * `useMobileDetection` uses for `isTouchDevice`).
 *
 * The drag guard keys on the draggable element's `data-item-drag`
 * attribute, never on a tag: a drag that starts on a child (the name, a
 * thumbnail) is still this item's drag, and one a nested draggable owns is
 * left to it. One hook per view; the returned builder is hook-free, for rows.
 */

import { useCallback, useEffect, useRef, type DragEvent } from 'react';
import { useMediaQuery } from '@chakra-ui/react';
import type { WorkspaceItem } from '@bower/shared';

import { useAppToast } from '../../../hooks/ui/useAppToast';
import { useSelectionStore } from '../../../stores/selectionStore';
import { writeItemDragPayload, type DraggedItem } from '../../navigation/HierarchyTree.dnd';
import { resetDropTarget, setDraggingItems } from './dragState';

export const TOUCH_PRIMARY_QUERY = '(hover: none) and (pointer: coarse)';

export interface ItemDragProps {
  draggable: boolean;
  'data-item-drag'?: string;
  onDragStart?: (event: DragEvent<HTMLElement>) => void;
  onDragEnd?: () => void;
}

/** An item as a drag carries it (the payload and the drag state). */
export const toDragged = (item: WorkspaceItem): DraggedItem => {
  const { id, kind, parentId, name } = item;
  return item.kind === 'file'
    ? { id, kind, parentId, name, artifactType: item.artifactType }
    : { id, kind, parentId, name };
};

// Literal semantic-token variables (the chip is plain DOM, outside Chakra), so
// the theme guard can check every name exists.
const CHIP_COLORS = {
  border: 'var(--chakra-colors-border-accent)',
  surface: 'var(--chakra-colors-bg-surface)',
  text: 'var(--chakra-colors-text-primary)',
  badge: 'var(--chakra-colors-brand-primary)',
  badgeText: 'var(--chakra-colors-text-inverse)',
} as const;

/**
 * The drag image (board `Main`): a tilted chip with the item's own visual (a
 * clone of the tile's thumbnail or glyph), its name, and a count badge when
 * several items move. Built in theme tokens and only briefly in the DOM, long
 * enough for the browser to snapshot it; `aria-hidden`, since it is a picture
 * of the drag and never content.
 */
function setDragChip(dataTransfer: DataTransfer, source: Element, name: string, count: number) {
  const chip = document.createElement('div');
  chip.setAttribute('aria-hidden', 'true');
  Object.assign(chip.style, {
    position: 'fixed',
    top: '-1000px',
    left: '-1000px',
    height: '36px',
    maxWidth: '320px',
    boxSizing: 'border-box',
    padding: '0 10px 0 4px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderRadius: '8px',
    border: `1px solid ${CHIP_COLORS.border}`,
    background: CHIP_COLORS.surface,
    color: CHIP_COLORS.text,
    boxShadow: 'var(--chakra-shadows-elevation-raised)',
    transform: 'rotate(-3deg)',
    font: '600 13px var(--chakra-fonts-body)',
    whiteSpace: 'nowrap',
  });

  const visual = source.querySelector('img, svg');
  const frame = document.createElement('span');
  frame.setAttribute('data-drag-chip-visual', '');
  Object.assign(frame.style, {
    width: '36px',
    height: '28px',
    flexShrink: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    borderRadius: '4px',
  });
  if (visual) {
    const copy = visual.cloneNode(true) as HTMLElement | SVGElement;
    copy.removeAttribute('loading');
    Object.assign(copy.style, { maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' });
    frame.appendChild(copy);
  }
  chip.appendChild(frame);

  const label = document.createElement('span');
  label.setAttribute('data-drag-chip-name', '');
  label.textContent = name;
  Object.assign(label.style, { overflow: 'hidden', textOverflow: 'ellipsis' });
  chip.appendChild(label);

  if (count > 1) {
    const badge = document.createElement('span');
    badge.setAttribute('data-drag-chip-count', '');
    badge.textContent = String(count);
    Object.assign(badge.style, {
      minWidth: '20px',
      height: '20px',
      padding: '0 6px',
      boxSizing: 'border-box',
      borderRadius: '9999px',
      background: CHIP_COLORS.badge,
      color: CHIP_COLORS.badgeText,
      fontSize: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    });
    chip.appendChild(badge);
  }

  document.body.appendChild(chip);
  dataTransfer.setDragImage?.(chip, 20, 18);
  setTimeout(() => chip.remove(), 0);
}

export function useItemDrag(items: readonly WorkspaceItem[]) {
  const [isTouch] = useMediaQuery(TOUCH_PRIMARY_QUERY);
  const toast = useAppToast();
  // An item renamed in place is not draggable while its field is open, so a
  // drag in the text selects text instead of moving the item.
  const renamingId = useSelectionStore((s) => s.renaming?.id ?? null);
  // Read at drag start, so the builder stays stable while pages load.
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  return useCallback(
    (item: WorkspaceItem): ItemDragProps => {
      if (isTouch || !item.canEdit || item.id === renamingId) return { draggable: false };
      return {
        draggable: true,
        'data-item-drag': item.id,
        onDragStart: (event) => {
          const source =
            event.target instanceof Element ? event.target.closest('[data-item-drag]') : null;
          if (source !== event.currentTarget) return;
          const { selectedIds } = useSelectionStore.getState();
          const dragged = selectedIds.has(item.id)
            ? itemsRef.current.filter((i) => selectedIds.has(i.id))
            : [item];
          if (dragged.some((i) => !i.canEdit)) {
            event.preventDefault();
            toast({
              title: 'Nothing moved',
              description: "Some selected items can't be moved: you only have view access.",
              status: 'warning',
            });
            return;
          }
          // Copy too: a drop on the chat composer adds the items as context
          // and moves nothing.
          event.dataTransfer.effectAllowed = 'copyMove';
          const payload = dragged.map(toDragged);
          writeItemDragPayload(event.dataTransfer, payload);
          setDraggingItems(payload);
          setDragChip(event.dataTransfer, event.currentTarget, item.name, dragged.length);
        },
        onDragEnd: () => {
          setDraggingItems([]);
          resetDropTarget();
        },
      };
    },
    [isTouch, toast, renamingId],
  );
}
